import React, { useEffect, useState, useCallback, useRef } from "react";
import { Button, Switch } from "@mui/material";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { grey } from "@mui/material/colors";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash.debounce";

import Loader from "../../components/loader/Loader";
import { PhotoProvider, PhotoView } from "react-photo-view";
import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
  storeBulkList,
} from "../../redux/counterSlice";
import {
  useTablePagination,
  useTableHeight,
} from "../../hooks/useTablePagination";
import BulkModal from "./BulkModal";
import { useForm } from "react-hook-form";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { usePermission } from "../../hooks/usePermission";

function Bulk() {
  const dispatch = useDispatch();
  const { bulkList = [] } = useSelector((state) => state.counter);
  const [loader, setLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const selectedRowRef = useRef(null);
  const [productList, setProductList] = useState([]);
  const permissions = usePermission("Bulk Provision");
  const hasActionPermission = permissions?.edit || permissions?.delete;

  const methods = useForm();
  const { reset } = methods;
  const {
    page,
    rowsPerPage,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("bulk", 10);
  const tableHeight = useTableHeight();

  const debouncedSearchChange = useCallback(
    debounce((value) => onSearchChange(value), 300),
    [],
  );
  useEffect(() => {
    if (searchText === "," || searchText === null || searchText === undefined) {
      onSearchChange("");
    }
  }, []);
  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    reset();
  };
  useEffect(() => {
  selectedRowRef.current = selectedRow;
}, [selectedRow]);

const loadInitialData = async () => {
  try {
    setLoader(true);
    const response = await axios.get("/bulk-provision");
    const freshList = response.data?.data || [];
    dispatch(storeBulkList(freshList));

    // ✅ Use ref instead of state to get latest selectedRow
    if (selectedRowRef.current) {
      const updatedRow = freshList.find((item) => item.id === selectedRowRef.current.id);
      if (updatedRow) setSelectedRow({ ...updatedRow });
    }
  } catch (err) {
    toast.error(err?.message || "Failed to fetch bulk data");
  } finally {
    setLoader(false);
    dispatch(hideConfirmModal({ msg: "" }));
  }
};

  const loadProducts = async () => {
    setLoader(true);
    try {
      const res = await axios.get("/activeProductList");
      setProductList(res?.data?.data || []);
    } catch (err) {
      toast.error(err?.message);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [page, rowsPerPage]);
  useEffect(() => {
    loadProducts();
  }, []);
  const toggleStatus = (id, currentStatus) => {
    setLoader(true);
    axios
      .get(`/bulk-product-switch/${id}`)
      .then(() => {
        toast.success(currentStatus === 1 ? "Deactivated" : "Activated");
        loadInitialData();
      })
      .catch((err) => toast.error(err?.message || "Failed to toggle status"))
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };

  const editClickHandler = (item) => {
    setSelectedRow({ ...item });
    setOpen(true);
  };
  // const deleteHandler = async (id) => {
  //   if (!id) return;

  //   dispatch(
  //     showConfirmModal({
  //       msg: "Are you sure you want to delete this bulk provision?",
  //       action: async () => {
  //         try {
  //           setLoader(true);
  //           await axios.delete(`/bulk-provision/${id}`); // Adjust API endpoint accordingly
  //           toast.success("Deleted successfully");
  //           loadInitialData();
  //         } catch (error) {
  //           toast.error(error?.response?.data?.message || "Failed to delete");
  //         } finally {
  //           setLoader(false);
  //           dispatch(hideConfirmModal({ msg: "" }));
  //         }
  //       },
  //     })
  //   );
  // };

  const filteredBulkList = bulkList.filter((item) =>
    item?.product?.product_name
      ?.toLowerCase()
      ?.includes(searchText.toLowerCase()),
  );

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{dataIndex + 1}</p>,
      },
    },
    {
      name: "image",
      label: "Image",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const imageUrl =
            filteredBulkList[dataIndex]?.product?.app_image?.app_product_image;
          return imageUrl ? (
            <PhotoProvider>
              <PhotoView key={dataIndex} src={imageUrl}>
                <img
                  src={imageUrl}
                  alt=""
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                />
              </PhotoView>
            </PhotoProvider>
          ) : (
            <span>-</span>
          );
        },
      },
    },
    {
      name: "product_name",
      label: "Addon Name",
      options: {
        customBodyRenderLite: (dataIndex) =>
          filteredBulkList[dataIndex]?.product?.product_name || "-",
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const item = filteredBulkList[dataIndex];
          const status = item?.active_status;
          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() =>
                dispatch(
                  showConfirmModal({
                    msg: status === 1 ? "Deactivate" : "Activate",
                    action: () => toggleStatus(item.id, status),
                  }),
                )
              }
              style={{ marginBottom: 5 }}
            />
          );
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            options: {
              customBodyRenderLite: (dataIndex) => {
                const item = filteredBulkList[dataIndex];
                return (
                  <div className="actionBtns">
                    {permissions?.edit && (
                      <Button
                        disabled={item.active_status === 1}
                        onClick={() => editClickHandler(item)}
                      >
                        <EditIcon
                          fontSize="medium"
                          sx={{
                            color:
                              item.active_status === 1
                                ? grey[600]
                                : "secondary.main",
                          }}
                        />
                      </Button>
                    )}
                    {/* <Button
                disabled={item.active_status === 1}
                onClick={() => deleteHandler(item.id)}
              >
                <DeleteOutlineOutlinedIcon color="error" fontSize="medium" />
              </Button> */}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: false,
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    rowsPerPage,
    page,
    onChangePage,
    onChangeRowsPerPage,
    customSearch: () => true,
    onSearchChange: debouncedSearchChange,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    searchText,
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No data available",
      },
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Bulk Provisioning</h4>
          <MUIDataTable
            data={filteredBulkList}
            columns={columns}
            options={options}
            title={
              permissions.add ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setSelectedRow(null);
                    setOpen(true);
                    reset();
                  }}
                  startIcon={<AddIcon />}
                >
                  New Product
                </Button>
              ) : null
            }
          />
        </>
      )}

      <BulkModal
        open={open}
        handleClose={handleClose}
        selectedRow={selectedRow}
        loadInitialData={loadInitialData}
        data={productList}
      />
    </>
  );
}

export default Bulk;
